import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, MousePointerClick, PackageOpen, ShieldCheck } from "lucide-react";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { iconList } from "../../../../utils/iconList";
import { cn } from "../../../../utils/cn";
import "../ModernIntegrations.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Alta de tienda WooCommerce/PrestaShop con el connect-flow: el portal ya NO
// pide la URL de la tienda — la vinculación la inicia el propio plugin con el
// botón "Conectar con BearTrack" (la tienda reporta su URL real y recibe su
// clave sola; ver ConnectStoreView). Este modal es la guía del paso a paso.
//
// La vía manual (registrar URL + pegar clave) sigue existiendo en los ajustes
// del plugin como alternativa, pero dejó de ser el camino que promovemos.
// ─────────────────────────────────────────────────────────────────────────────

// update.json de gh-pages trae SIEMPRE la download_url vigente por plataforma.
const PLATFORMS = {
  WooCommerce: {
    icon: "woo",
    updateUrl: "https://beartrackapp.github.io/woocommerce-connector/update.json",
    downloadLabel: "Descargar plugin WooCommerce",
    installStep: "Instálalo en tu WordPress: Plugins → Añadir nuevo → Subir plugin.",
    connectStep: "En tu tienda ve a WooCommerce → Beartrack y presiona \"Conectar con BearTrack\".",
  },
  Prestashop: {
    icon: "prestashop",
    updateUrl: "https://beartrackapp.github.io/prestashop-connector/update.json",
    downloadLabel: "Descargar módulo PrestaShop",
    installStep: "Instálalo en tu Back Office: Módulos → Subir un módulo.",
    connectStep: "En Módulos → BearTrack → Configurar, presiona \"Conectar con BearTrack\".",
  },
};

export default function AddStoreConnectModal({ open, platform, onClose }) {
  const { t } = useTranslation();
  const cfg = PLATFORMS[platform] ?? PLATFORMS.WooCommerce;

  const [downloadUrl, setDownloadUrl] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);
  const [downloadError, setDownloadError] = useState(false);
  const [downloadRetry, setDownloadRetry] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    const ctrl = new AbortController();
    setDownloadError(false);
    (async () => {
      try {
        const res = await fetch(cfg.updateUrl, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json?.download_url) throw new Error("Campo download_url ausente");
        setDownloadUrl(json.download_url);
        if (json?.version) setLatestVersion(json.version);
      } catch (e) {
        if (e?.name === "AbortError") return;
        console.error("update.json error:", e);
        setDownloadError(true);
      }
    })();
    return () => ctrl.abort();
  }, [open, cfg.updateUrl, downloadRetry]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t("Conectar {{platform}}", { platform })}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t("Entendido")}
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="mig-logo" aria-hidden="true">
            <img src={iconList[cfg.icon]} alt="" className="mig-logo__img dark-safe-img" />
          </span>
          <p className="text-sm text-fg-muted">
            {t(
              "Ya no necesitas registrar la URL de tu tienda: la conexión se hace en un clic desde el propio plugin.",
            )}
          </p>
        </div>

        {downloadUrl ? (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer noopener"
            className={cn(
              "inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
              "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700",
            )}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {latestVersion
              ? `${t(cfg.downloadLabel)} v${latestVersion}`
              : t(cfg.downloadLabel)}
          </a>
        ) : downloadError ? (
          <div
            className="flex flex-col items-center gap-2 rounded-lg border border-border bg-bg-muted px-4 py-3 sm:flex-row sm:justify-between"
            role="alert"
          >
            <p className="text-sm text-fg-muted">{t("No pudimos obtener el enlace de descarga.")}</p>
            <Button variant="outline" size="sm" onClick={() => setDownloadRetry((k) => k + 1)}>
              {t("Reintentar")}
            </Button>
          </div>
        ) : (
          <span
            className="inline-flex h-10 w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-bg-muted px-4 text-sm font-medium text-fg-subtle"
            role="status"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {t("Cargando enlace...")}
          </span>
        )}

        <ol className="space-y-3">
          {[
            { icon: <PackageOpen className="h-4 w-4" aria-hidden="true" />, text: t(cfg.installStep) },
            { icon: <MousePointerClick className="h-4 w-4" aria-hidden="true" />, text: t(cfg.connectStep) },
            {
              icon: <ShieldCheck className="h-4 w-4" aria-hidden="true" />,
              text: t(
                "Autoriza la conexión en la pantalla de Beartrack que se abrirá: la tienda queda vinculada al instante y aparecerá en esta lista automáticamente.",
              ),
            },
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-3 rounded-lg border border-border bg-bg px-3 py-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-bg-muted text-xs font-semibold text-fg">
                {i + 1}
              </span>
              <span className="flex items-start gap-2 text-sm text-fg">
                <span className="mt-0.5 shrink-0 text-fg-muted">{step.icon}</span>
                {step.text}
              </span>
            </li>
          ))}
        </ol>

        <p className="text-xs text-fg-muted">
          {t(
            "Debes tener sesión iniciada en este portal en el mismo navegador al presionar \"Conectar con BearTrack\". ¿Prefieres el método manual? En los ajustes del plugin también puedes pegar una clave de conexión.",
          )}
        </p>
      </div>
    </Modal>
  );
}
